/**
 * The employment breakdown a licence application carries
 * (`zanzibaris*`/`tanzaniaMainlanders*`/`foreigners*` on LicenseRequestDTO).
 *
 * No customer-facing screen captures these numbers, so for most applications
 * every one of them is absent. A group is therefore rendered only when the API
 * actually returned figures for it — a grid of "N/A Male, N/A Female" with a
 * "0 Employees" total reads as broken data and invents a headcount that was
 * never recorded.
 */

export interface EmployeeStats {
    male: number;
    female: number;
}

export interface WorkforceCategory {
    management: EmployeeStats;
    skilled: EmployeeStats;
    unskilled: EmployeeStats;
    totalEmployees: number;
}

interface WorkforceStatsProps {
    zanzibaris?: WorkforceCategory;
    mainlanders?: WorkforceCategory;
    foreigners?: WorkforceCategory;
}

const StatBlock = ({ label, stats }: { label: string; stats: EmployeeStats | number }) => (
    <div>
        <span className="text-gray-500 text-md">{label}</span>
        <p className="text-[#2C2C2C] font-medium mt-1">
            {typeof stats === 'number'
                ? `${stats} Employees`
                : `${stats.male} Male, ${stats.female} Female`}
        </p>
    </div>
);

const WorkforceGroup = ({
    title,
    stats
}: {
    title: string;
    stats: WorkforceCategory
    }) => (

        <div className="mb-5">
            <h2 className="text-xl font-semibold text-[#2C2C2C] mb-4">{title}</h2>
            <div className="grid grid-cols-2 gap-x-24 gap-y-6">
                <StatBlock label="Management" stats={stats.management} />
                <StatBlock label="Skilled" stats={stats.skilled} />
                <StatBlock label="Unskilled" stats={stats.unskilled} />
                <StatBlock label="Total Workforce" stats={stats.totalEmployees} />
            </div>
        </div>

);

export default function LicenceWorkforceDetails({
    zanzibaris,
    mainlanders,
    foreigners
}: WorkforceStatsProps) {
    const groups: Array<[string, WorkforceCategory | undefined]> = [
        ["Zanzibaris", zanzibaris],
        ["Tanzania Mainlanders", mainlanders],
        ["Foreigners", foreigners],
    ];
    const recorded = groups.filter(([, stats]) => stats !== undefined);

    return (
        <div>
            <h1 className="text-lg font-semibold text-[#3C1E03] mb-2">Workforce</h1>
            <div className="bg-white shadow-md p-8 rounded-xl">
                {recorded.length === 0 ? (
                    <p className="text-sm text-gray-500">
                        No workforce details recorded for this application.
                    </p>
                ) : (
                    recorded.map(([title, stats]) => (
                        <WorkforceGroup key={title} title={title} stats={stats as WorkforceCategory} />
                    ))
                )}
            </div>
        </div>
    );
};
